import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  ShieldCheck,
  CheckCircle,
  ArrowLeft,
  Trash2,
  Flower,
  FileText,
  ShoppingBag,
  Plus,
  Upload,
  Calendar,
  Layers,
  DollarSign,
  MapPin,
  Sparkles,
  Heart,
  LogOut,
  AlertCircle,
  XCircle,
  BarChart2,
} from "lucide-react";
import Dashboard from "./Dashboard";

import { db as database } from "./firebase";
import { ref, onValue, update, remove } from "firebase/database";

const ADMIN_PASSWORD = "luungocbao";

export default function Admin() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("orders");

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [loginError, setLoginError] = useState("");

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    message: "",
    onConfirm: null,
  });

  const [allOrders, setAllOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);

  const [communityPending, setCommunityPending] = useState([]);
  const [communityApproved, setCommunityApproved] = useState([]);

  const [flowers, setFlowers] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [flowerForm, setFlowerForm] = useState({
    id: "",
    page: "",
    ingredients: "",
    nameVi: "",
    nameEn: "",
    meaning: "",
    type: "",
    color: "",
    season: "",
    storage: "",
    careLevel: "Dễ",
    freshTime: "",
    price: "",
    origin: "",
    occasion: "",
    receiver: "",
    matching: "",
  });

  const [posts, setPosts] = useState([]);
  const [postForm, setPostForm] = useState({
    title: "",
    category: "",
    content: "",
  });

  useEffect(() => {
    const ordersRef = ref(database, "orders");
    const communityRef = ref(database, "community");

    const unsubscribeOrders = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ordersArray = Object.keys(data).map((key) => ({
          ...data[key],
          firebaseKey: key, 
          status: data[key].status || "pending_confirmation", 
        }));

        setAllOrders(ordersArray);

        const filteringPending = ordersArray.filter(
          (o) =>
            o.status === "pending_confirmation" ||
            o.status === "customer_paid_checking" ||
            o.status === "awaiting_payment"
        );
        filteringPending.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setPendingOrders(filteringPending);

        const filteringCompleted = ordersArray.filter(
          (o) => o.status === "verified_success"
        );
        filteringCompleted.sort(
          (a, b) =>
            new Date(b.approvedAt || b.createdAt) -
            new Date(a.approvedAt || a.createdAt)
        );
        setCompletedOrders(filteringCompleted);
      } else {
        setPendingOrders([]);
      }
    });

    const unsubscribeCommunity = onValue(communityRef, (snapshot) => {
      const data = snapshot.val();
      const normalizeArray = (node) => {
        if (!node) return [];
        return Object.keys(node).map((key) => ({
          ...node[key],
          firebaseKey: key,
        }));
      };
      const pending = normalizeArray(data?.pendingPosts || {});
      const approved = normalizeArray(data?.approvedPosts || {});

      pending.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      approved.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setCommunityPending(pending);
      setCommunityApproved(approved);
    });

    const savedFlowers = JSON.parse(localStorage.getItem("sellixy_flowers")) || [];
    const savedPosts = JSON.parse(localStorage.getItem("sellixy_posts")) || [];
    setFlowers(savedFlowers);
    setPosts(savedPosts);

    return () => {
      unsubscribeOrders();
      unsubscribeCommunity();
    };
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  };

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setIsLoggedIn(true);
      setLoginError("");
      showToast("Đăng nhập hệ thống quản trị thành công!", "success");
    } else {
      setLoginError("Mật khẩu không chính xác. Vui lòng thử lại!");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFlowerForm({ ...flowerForm, id: file.name });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // CẬP NHẬT: DUYỆT ĐƠN HÀNG - GHI VÀO CẢ NHÁNH ADMIN VÀ NHÁNH USER KHÁCH HÀNG
  const approveOrder = (id) => {
    const orderToApprove = allOrders.find((o) => o.id === id);
    const userId = orderToApprove?.userId || "khach_vang_lai";

    const updates = {};
    updates[`orders/${id}/status`] = "verified_success";
    updates[`orders/${id}/approvedAt`] = new Date().toISOString();

    if (userId !== "khach_vang_lai") {
      updates[`user_orders/${userId}/${id}/status`] = "verified_success";
      updates[`user_orders/${userId}/${id}/approvedAt`] = new Date().toISOString();
    }

    const rootRef = ref(database);
    update(rootRef, updates)
      .then(() => {
        showToast("Đơn hàng đã được xác nhận và duyệt thành công!", "success");
      })
      .catch((err) => {
        console.error("Lỗi duyệt đơn:", err);
        showToast("Không thể kết nối Firebase!", "error");
      });
  };

  // CẬP NHẬT: XÓA / TỪ CHỐI ĐƠN HÀNG - XÓA / TỪ CHỐI CẢ Ở 2 NƠI
  const deleteOrder = (id) => {
    setConfirmModal({
      show: true,
      message: "Bạn có chắc chắn muốn từ chối giao dịch này?",
      onConfirm: () => {
        const orderToReject = allOrders.find((o) => o.id === id);
        const userId = orderToReject?.userId || "khach_vang_lai";

        const updates = {};
        updates[`orders/${id}/status`] = "payment_rejected";
        updates[`orders/${id}/rejectedAt`] = new Date().toISOString();

        if (userId !== "khach_vang_lai") {
          updates[`user_orders/${userId}/${id}/status`] = "payment_rejected";
          updates[`user_orders/${userId}/${id}/rejectedAt`] = new Date().toISOString();
        }

        const rootRef = ref(database);
        update(rootRef, updates)
          .then(() => {
            setConfirmModal({ show: false, message: "", onConfirm: null });
            showToast("Đã từ chối giao dịch và gửi thông báo cho khách!", "error");
          })
          .catch((err) => {
            console.error("Lỗi từ chối đơn:", err);
            showToast("Không thể cập nhật trạng thái giao dịch!", "error");
          });
      },
    });
  };

  const approveCommunityPost = (post) => {
    if (!post?.firebaseKey) {
      showToast("Không tìm thấy bài cộng đồng để duyệt!", "error");
      return;
    }
    const approvedRef = ref(database, `community/approvedPosts/${post.firebaseKey}`);
    const pendingRef = ref(database, `community/pendingPosts/${post.firebaseKey}`);

    const approvedPost = {
      ...post,
      approved: true,
      approvedAt: new Date().toISOString(),
      status: "approved",
    };

    update(approvedRef, approvedPost)
      .then(() => remove(pendingRef))
      .then(() => {
        showToast("Đã duyệt bài cộng đồng thành công!", "success");
      })
      .catch((err) => {
        console.error("Lỗi duyệt bài cộng đồng:", err);
        showToast("Không thể duyệt bài cộng đồng!", "error");
      });
  };

  const rejectCommunityPost = (post) => {
    if (!post?.firebaseKey) {
      showToast("Không tìm thấy bài cộng đồng để xóa!", "error");
      return;
    }
    const pendingRef = ref(database, `community/pendingPosts/${post.firebaseKey}`);
    remove(pendingRef)
      .then(() => {
        showToast("Đã từ chối bài cộng đồng!", "error");
      })
      .catch((err) => {
        console.error("Lỗi từ chối bài cộng đồng:", err);
        showToast("Không thể xóa bài chờ duyệt!", "error");
      });
  };

  const handleAddFlower = (e) => {
    e.preventDefault();
    if (!flowerForm.id || !flowerForm.nameVi) {
      showToast("Vui lòng tải ảnh hoa hoặc điền Mã hoa và Tên hoa tiếng Việt!", "warning");
      return;
    }
    const updatedFlowers = [...flowers, { ...flowerForm, keyId: Date.now() }];
    localStorage.setItem("sellixy_flowers", JSON.stringify(updatedFlowers));
    setFlowers(updatedFlowers);
    showToast("Thêm thông tin hoa mới vào kho thành công!", "success");
    setFlowerForm({
      id: "",
      page: "",
      ingredients: "",
      nameVi: "",
      nameEn: "",
      meaning: "",
      type: "",
      color: "",
      season: "",
      storage: "",
      careLevel: "Dễ",
      freshTime: "",
      price: "",
      origin: "",
      occasion: "",
      receiver: "",
      matching: "",
    });
    setImagePreview(null);
  };

  const deleteFlower = (keyId) => {
    setConfirmModal({
      show: true,
      message: "Bạn có chắc chắn muốn gỡ bỏ hoàn toàn loại hoa này khỏi danh mục kho?",
      onConfirm: () => {
        const updated = flowers.filter((f) => f.keyId !== keyId);
        localStorage.setItem("sellixy_flowers", JSON.stringify(updated));
        setFlowers(updated);
        setConfirmModal({ show: false, message: "", onConfirm: null });
        showToast("Đã gỡ bỏ thông tin hoa thành công!", "error");
      },
    });
  };

  const handleAddPost = (e) => {
    e.preventDefault();
    if (!postForm.title || !postForm.content) {
      showToast("Vui lòng điền tiêu đề và nội dung bài viết!", "warning");
      return;
    }
    const newPost = {
      ...postForm,
      id: Date.now(),
      date: new Date().toLocaleDateString("vi-VN"),
    };
    const updatedPosts = [...posts, newPost];
    localStorage.setItem("sellixy_posts", JSON.stringify(updatedPosts));
    setPosts(updatedPosts);
    showToast("Đăng bài viết mới truyền cảm hứng thành công!", "success");
    setPostForm({ title: "", category: "", content: "" });
  };

  const deletePost = (id) => {
    setConfirmModal({
      show: true,
      message: "Bạn có chắc chắn muốn xóa bài viết đã xuất bản này không?",
      onConfirm: () => {
        const updated = posts.filter((p) => p.id !== id);
        localStorage.setItem("sellixy_posts", JSON.stringify(updated));
        setPosts(updated);
        setConfirmModal({ show: false, message: "", onConfirm: null });
        showToast("Đã xóa bài viết khỏi hệ thống tin tức!", "error");
      },
    });
  };

  if (!isLoggedIn) {
    return (
      <div style={styles.adminContainer}>
        <AnimatePresence>
          {toast.show && (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                ...styles.toastNotification,
                ...(toast.type === "success" ? styles.toastSuccess : styles.toastError),
              }}
            >
              {toast.type === "success" ? (
                <CheckCircle size={18} />
              ) : (
                <AlertCircle size={18} />
              )}
              <span>{toast.message}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 100 }}
          style={styles.loginCard}
        >
          <div style={styles.iconCircle}>
            <Lock size={26} color="#fff" />
          </div>
          <h2 style={styles.loginTitle}>Hệ thống Quản trị</h2>
          <p style={styles.loginSubtitle}>
            Vui lòng nhập mật khẩu cấp quyền kiểm trị hệ thống Sellixy
          </p>
          <input
            type="password"
            placeholder="Mật khẩu bảo mật..."
            style={{
              ...styles.adminInput,
              ...(loginError ? styles.adminInputError : {}),
            }}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (loginError) setLoginError("");
            }}
            onKeyPress={(e) => e.key === "Enter" && handleLogin()}
          />

          <AnimatePresence>
            {loginError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                style={styles.inlineErrorBox}
              >
                <XCircle size={14} style={{ marginRight: 6, flexShrink: 0 }} />
                <span>{loginError}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <button style={styles.btnAction} onClick={handleLogin}>
            Xác thực truy cập
          </button>
          <p onClick={() => navigate("/")} style={styles.backText}>
            <ArrowLeft size={14} /> Trở lại trang chủ cửa hàng
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={styles.dashboardContainer}>
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{
              position: "fixed",
              top: "80px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                ...styles.toastContainer,
                ...(toast.type === "success"
                  ? styles.toastContainerSuccess
                  : toast.type === "warning"
                  ? styles.toastContainerWarning
                  : styles.toastContainerError),
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {toast.type === "success" ? (
                  <CheckCircle size={16} />
                ) : toast.type === "warning" ? (
                  <AlertCircle size={16} />
                ) : (
                  <XCircle size={16} />
                )}
                <span>{toast.message}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmModal.show && (
          <div style={styles.modalOverlay}>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              style={styles.modalBox}
            >
              <div style={styles.modalHeaderIcon}>
                <AlertCircle size={28} color="#ef4444" />
              </div>
              <h4 style={styles.modalTitle}>Xác nhận hành động</h4>
              <p style={styles.modalMessage}>{confirmModal.message}</p>
              <div style={styles.modalButtonGroup}>
                <button
                  style={styles.btnModalCancel}
                  onClick={() =>
                    setConfirmModal({ show: false, message: "", onConfirm: null })
                  }
                >
                  Hủy bỏ
                </button>
                <button
                  style={styles.btnModalConfirm}
                  onClick={confirmModal.onConfirm}
                >
                  Đồng ý thực hiện
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <header style={styles.adminHeader}>
        <div style={styles.headerLeft}>
          <div style={styles.logoBadge}>
            <ShieldCheck size={22} color="#fff" />
          </div>
          <div>
            <h2 style={styles.headerTitle}>Floraison Control Panel</h2>
            <span style={styles.headerSubtitle}>Chào mừng quay trở lại, Admin</span>
          </div>
        </div>

        <div style={styles.tabGroup}>
          <button
            style={{
              ...styles.btnTab,
              ...(activeTab === "dashboard" ? styles.btnTabActive : {}),
            }}
            onClick={() => setActiveTab("dashboard")}
          >
            <BarChart2 size={16} /> Dashboard
          </button>
          <button
            style={{
              ...styles.btnTab,
              ...(activeTab === "orders" ? styles.btnTabActive : {}),
            }}
            onClick={() => setActiveTab("orders")}
          >
            <ShoppingBag size={16} /> Đơn hàng
          </button>
          <button
            style={{
              ...styles.btnTab,
              ...(activeTab === "flowers" ? styles.btnTabActive : {}),
            }}
            onClick={() => setActiveTab("flowers")}
          >
            <Flower size={16} /> Danh mục hoa
          </button>
          <button
            style={{
              ...styles.btnTab,
              ...(activeTab === "posts" ? styles.btnTabActive : {}),
            }}
            onClick={() => setActiveTab("posts")}
          >
            <FileText size={16} /> Tin tức & Post
          </button>
          <button
            style={{
              ...styles.btnTab,
              ...(activeTab === "community" ? styles.btnTabActive : {}),
            }}
            onClick={() => setActiveTab("community")}
          >
            <Heart size={16} /> Cộng đồng
          </button>
        </div>

        <button
          style={styles.btnLogout}
          onClick={() => {
            showToast("Đã đăng xuất an toàn!", "success");
            setTimeout(() => {
              setIsLoggedIn(false);
              setPassword("");
            }, 3000);
          }}
        >
          <LogOut size={16} /> Đăng xuất
        </button>
      </header>

      <div style={styles.contentWrap}>
        {activeTab === "dashboard" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Dashboard orders={allOrders} />
          </motion.div>
        )}

        {activeTab === "orders" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div style={styles.statsRow}>
              <div style={styles.statBox}>
                <span style={styles.statLabel}>Đơn chờ duyệt</span>
                <strong style={styles.statValue}>{pendingOrders.length}</strong>
              </div>
              <div
                style={{
                  ...styles.statBox,
                  marginLeft: 16,
                  borderColor: "#10b981",
                }}
              >
                <span style={styles.statLabel}>Đơn đã thành công</span>
                <strong style={{ ...styles.statValue, color: "#10b981" }}>
                  {completedOrders.length}
                </strong>
              </div>
            </div>

            {pendingOrders.length === 0 ? (
              <div style={styles.emptyState}>
                <ShoppingBag size={48} style={{ opacity: 0.3, marginBottom: 15 }} />
                <p>Hệ thống hiện tại không có đơn hàng nào cần phê duyệt thanh toán.</p>
              </div>
            ) : (
              <div style={styles.orderList}>
                <AnimatePresence>
                  {pendingOrders.map((order) => (
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      key={order.id}
                      style={styles.orderCard}
                    >
                      <div style={styles.orderInfo}>
                        <div style={styles.orderMainHeader}>
                          <span style={styles.orderId}>#{order.id}</span>
                          <span style={styles.orderTime}>
                            {order.createdAt
                              ? new Date(order.createdAt).toLocaleTimeString("vi-VN")
                              : "Vừa xong"}
                          </span>
                          <span
  style={{
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "12px",
    fontWeight: "600",
    background:
      order.status === "customer_paid_checking"
        ? "#e0f2fe"
        : order.status === "awaiting_payment"
        ? "#f3e8ff" // Màu tím nhạt cho PayOS
        : "#fef3c7",
    color:
      order.status === "customer_paid_checking"
        ? "#0369a1"
        : order.status === "awaiting_payment"
        ? "#7e22ce"
        : "#b45309",
  }}
>
  {order.status === "customer_paid_checking"
    ? "Khách báo Đã chuyển khoản"
    : order.status === "awaiting_payment"
    ? "Đang chờ quét PayOS"
    : "Chờ thanh toán/COD"}
</span>
                        </div>
                        <h4 style={styles.customerName}>
                          {order.buyer?.name || "Ẩn danh"}
                        </h4>
                        <div style={styles.customerContact}>
                          <span>📞 {order.buyer?.phone}</span>
                          <span style={{ marginLeft: 15 }}>
                            📍 Giao tới: {order.receiver?.address} (
                            {order.receiver?.ward})
                          </span>
                        </div>
                        <div style={styles.itemList}>
                          {order.items?.map((item, idx) => (
                            <span key={idx} style={styles.itemTag}>
                              {item.name}{" "}
                              <strong style={{ color: "#8f86a8" }}>
                                x{item.quantity}
                              </strong>
                            </span>
                          ))}
                        </div>
                      </div>
                      <div style={styles.orderPriceWrap}>
                        <div style={styles.priceVal}>
                          {order.total?.toLocaleString()}đ
                        </div>
                        <div style={styles.actionGroup}>
                          <button
                            style={styles.btnApprove}
                            onClick={() => approveOrder(order.id)}
                          >
                            <CheckCircle size={16} /> Duyệt đơn
                          </button>
                          <button
                            style={styles.btnDelete}
                            onClick={() => deleteOrder(order.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {completedOrders.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <button
                  onClick={() => setShowCompleted((v) => !v)}
                  style={styles.completedToggle}
                >
                  <CheckCircle size={16} color="#10b981" /> Đơn hàng đã hoàn tất (
                  {completedOrders.length})
                  <span
                    style={{ marginLeft: "auto", fontSize: 12, color: "#8a8a93" }}
                  >
                    {showCompleted ? "▲ Thu gọn" : "▼ Xem tất cả"}
                  </span>
                </button>
                <AnimatePresence>
                  {showCompleted && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      style={{ overflow: "hidden" }}
                    >
                      <div style={{ ...styles.orderList, marginTop: 16 }}>
                        {completedOrders.map((order) => (
                          <div key={order.id} style={styles.completedCard}>
                            <div style={styles.orderInfo}>
                              <div style={styles.orderMainHeader}>
                                <span style={styles.orderId}>#{order.id}</span>
                                <span style={styles.orderTime}>
                                  {order.createdAt
                                    ? new Date(order.createdAt).toLocaleDateString(
                                        "vi-VN"
                                      )
                                    : ""}
                                </span>
                                <span style={styles.successBadge}>
                                  ✓ Đã duyệt thành công
                                </span>
                                {order.approvedAt && (
                                  <span style={{ fontSize: 11, color: "#8a8a93" }}>
                                    Duyệt lúc{" "}
                                    {new Date(order.approvedAt).toLocaleTimeString(
                                      "vi-VN",
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }
                                    )}{" "}
                                    {new Date(order.approvedAt).toLocaleDateString(
                                      "vi-VN"
                                    )}
                                  </span>
                                )}
                              </div>
                              <h4 style={styles.customerName}>
                                {order.buyer?.name || "Ẩn danh"}
                              </h4>
                              <div style={styles.customerContact}>
                                <span>📞 {order.buyer?.phone}</span>
                                <span style={{ marginLeft: 15 }}>
                                  📍 {order.receiver?.address} ({order.receiver?.ward})
                                </span>
                              </div>
                              <div style={styles.itemList}>
                                {order.items?.map((item, idx) => (
                                  <span key={idx} style={styles.itemTag}>
                                    {item.name}{" "}
                                    <strong style={{ color: "#10b981" }}>
                                      x{item.quantity}
                                    </strong>
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div style={styles.orderPriceWrap}>
                              <div style={{ ...styles.priceVal, color: "#10b981" }}>
                                {order.total?.toLocaleString()}đ
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "flowers" && (
          <div style={styles.gridTwoColumns}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              style={styles.stickyColumn}
            >
              <h3 style={styles.sectionTitle}>Cập nhật thông tin hoa</h3>
              <form onSubmit={handleAddFlower} style={styles.formContainer}>
                <div style={styles.uploadSection}>
                  <label style={styles.uploadLabel}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                    <div style={styles.uploadPlaceholder}>
                      <div style={styles.uploadIconCircle}>
                        <Upload size={20} color="#8f86a8" />
                      </div>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1e1e24",
                        }}
                      >
                        Tải ảnh thương phẩm
                      </span>
                      <span
                        style={{ fontSize: "12px", color: "#8a8a93", marginTop: 2 }}
                      >
                        Mã hoa tự gán bằng tên file
                      </span>
                    </div>
                  </label>
                  {imagePreview && (
                    <div style={styles.previewBox}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={styles.previewImg}
                      />
                      <div style={styles.previewTag}>Xem trước sản phẩm</div>
                    </div>
                  )}
                </div>

                <h4 style={styles.subFormTitle}>
                  <Sparkles size={14} /> Thông tin cốt lõi
                </h4>
                <div style={styles.formGrid}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Mã hoa (Tên file gốc)</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      value={flowerForm.id}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, id: e.target.value })
                      }
                      placeholder="Tự điền khi chọn ảnh hoặc gõ thủ công..."
                      required
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Tên hoa (Tiếng Việt)</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      value={flowerForm.nameVi}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, nameVi: e.target.value })
                      }
                      placeholder="VD: Hoa Hồng Đà Lạt"
                      required
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Tên hoa (Tiếng Anh)</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      value={flowerForm.nameEn}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, nameEn: e.target.value })
                      }
                      placeholder="VD: Red Rose"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Giá bán thương mại</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      value={flowerForm.price}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, price: e.target.value })
                      }
                      placeholder="VD: 150,000đ"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Hiển thị tại trang</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="VD: Trang chủ, Khuyến mãi..."
                      value={flowerForm.page}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, page: e.target.value })
                      }
                    />
                  </div>
                </div>

                <h4 style={{ ...styles.subFormTitle, marginTop: 20 }}>
                  <Layers size={14} /> Đặc tính thực vật & Chăm sóc
                </h4>
                <div style={styles.formGrid}>
                  <div>
                    <label style={labelStyle}>Dạng thành phẩm</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="Bó hoa / Cắt cành / Chậu..."
                      value={flowerForm.type}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, type: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Tông màu chủ đạo</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="VD: Pastel, Đỏ Nhung"
                      value={flowerForm.color}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, color: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Mùa sinh trưởng</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="VD: Quanh năm, Mùa Đông"
                      value={flowerForm.season}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, season: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Độ bền / Thời gian tươi</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="VD: 5 - 7 ngày"
                      value={flowerForm.freshTime}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, freshTime: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Nguồn gốc / Xuất xứ</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="VD: Nhập khẩu Ecuador"
                      value={flowerForm.origin}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, origin: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Mức độ chăm sóc</label>
                    <select
                      style={styles.selectStyle}
                      value={flowerForm.careLevel}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, careLevel: e.target.value })
                      }
                    >
                      <option value="Dễ">💚 Dễ chăm sóc</option>
                      <option value="Trung bình">💛 Cần lưu ý (Trung bình)</option>
                      <option value="Khó">❤️ Kỹ thuật cao (Khó)</option>
                    </select>
                  </div>
                </div>

                <h4 style={{ ...styles.subFormTitle, marginTop: 20 }}>
                  <Heart size={14} /> Ý nghĩa & Nghệ thuật phối bó
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Thành phần phụ tố của hoa</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="Các loại lá phối, phụ liệu..."
                      value={flowerForm.ingredients}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, ingredients: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Ý nghĩa biểu trưng</label>
                    <textarea
                      style={styles.textareaStyle}
                      placeholder="Thông điệp tình yêu, sự biết ơn..."
                      value={flowerForm.meaning}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, meaning: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Quy trình & Hướng dẫn lưu trữ</label>
                    <textarea
                      style={styles.textareaStyle}
                      placeholder="Nhiệt độ phòng, cách thay nước..."
                      value={flowerForm.storage}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, storage: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Dịp tặng phù hợp</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="Sinh nhật, Lễ kỷ niệm..."
                      value={flowerForm.occasion}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, occasion: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Đối tượng nhận tối ưu</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="Người yêu, Đối tác, Đồng nghiệp..."
                      value={flowerForm.receiver}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, receiver: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Sự kết hợp hoàn hảo khi phối bó</label>
                    <input
                      type="text"
                      style={styles.inputStyle}
                      placeholder="Phối hợp đẹp nhất với hoa Baby, Lá Bạc..."
                      value={flowerForm.matching}
                      onChange={(e) =>
                        setFlowerForm({ ...flowerForm, matching: e.target.value })
                      }
                    />
                  </div>
                </div>

                <button type="submit" style={styles.btnSubmitForm}>
                  <Plus size={18} /> Cập nhật sản phẩm vào kho
                </button>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h3 style={styles.sectionTitle}>Kho lưu trữ sản phẩm ({flowers.length})</h3>
              <div style={styles.flowerItemGrid}>
                {flowers.length === 0 ? (
                  <div style={{ ...styles.emptyState, gridColumn: "1/-1" }}>
                    Chưa có dữ liệu hoa được cấu hình.
                  </div>
                ) : (
                  flowers.map((flower) => (
                    <div key={flower.keyId} style={styles.flowerMiniCard}>
                      <div style={styles.flowerCardHeader}>
                        <div>
                          <h4 style={styles.flowerCardTitle}>{flower.nameVi}</h4>
                          <span style={styles.flowerCardSub}>
                            {flower.nameEn || "Không có tên tiếng Anh"}
                          </span>
                        </div>
                        <button
                          style={styles.btnDeleteMini}
                          onClick={() => deleteFlower(flower.keyId)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div style={styles.flowerMetaDetails}>
                        <div style={styles.metaLine}>
                          <DollarSign size={12} style={{ marginRight: 4 }} /> Giá:{" "}
                          {flower.price || "Chưa cập nhật"}
                        </div>
                        <div style={styles.metaLine}>
                          <Layers size={12} style={{ marginRight: 4 }} /> Phân loại:{" "}
                          {flower.type || "Chưa rõ"}
                        </div>
                        <div style={styles.metaLine}>
                          <MapPin size={12} style={{ marginRight: 4 }} /> Vị trí:{" "}
                          {flower.page || "Trang sản phẩm"}
                        </div>
                      </div>
                      <div style={styles.flowerIdBadge}>Mã tệp: {flower.id}</div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}

        {activeTab === "posts" && (
          <div style={styles.gridTwoColumns}>
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
              <h3 style={styles.sectionTitle}>Khởi tạo nội dung viết bài</h3>
              <form onSubmit={handleAddPost} style={styles.formContainer}>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Tiêu đề bài viết bản tin</label>
                  <input
                    type="text"
                    style={styles.inputStyle}
                    value={postForm.title}
                    onChange={(e) =>
                      setPostForm({ ...postForm, title: e.target.value })
                    }
                    placeholder="Nhập tiêu đề hấp dẫn..."
                    required
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Danh mục phân loại chủ đề</label>
                  <input
                    type="text"
                    style={styles.inputStyle}
                    placeholder="Ví dụ: Xu hướng nghệ thuật, Kiến thức chăm sóc hoa..."
                    value={postForm.category}
                    onChange={(e) =>
                      setPostForm({ ...postForm, category: e.target.value })
                    }
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Nội dung bài viết</label>
                  <textarea
                    style={{ ...styles.textareaStyle, height: 260 }}
                    placeholder="Viết nội dung câu chuyện truyền cảm hứng tại đây..."
                    value={postForm.content}
                    onChange={(e) =>
                      setPostForm({ ...postForm, content: e.target.value })
                    }
                    required
                  />
                </div>
                <button type="submit" style={styles.btnSubmitForm}>
                  <FileText size={18} /> Phát hành bài viết
                </button>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
              <h3 style={styles.sectionTitle}>Các ấn phẩm đã xuất bản ({posts.length})</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {posts.length === 0 ? (
                  <div style={styles.emptyState}>Hệ thống chưa có bài đăng nào được ghi nhận.</div>
                ) : (
                  posts.map((post) => (
                    <div key={post.id} style={styles.postCard}>
                      <div style={styles.postMainInfo}>
                        <span style={styles.postCategoryBadge}>
                          {post.category || "Tin tổng hợp"}
                        </span>
                        <h4 style={styles.postTitleText}>{post.title}</h4>
                        <div style={styles.postMetaBottom}>
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Calendar size={12} /> Ngày đăng: {post.date}
                          </span>
                        </div>
                      </div>
                      <button
                        style={styles.btnDeleteMini}
                        onClick={() => deletePost(post.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}

        {activeTab === "community" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div style={styles.statsRow}>
              <div style={styles.statBox}>
                <span style={styles.statLabel}>Bài chờ duyệt</span>
                <strong style={styles.statValue}>{communityPending.length}</strong>
              </div>
              <div
                style={{
                  ...styles.statBox,
                  marginLeft: 16,
                  borderColor: "#10b981",
                }}
              >
                <span style={styles.statLabel}>Bài đã duyệt</span>
                <strong style={{ ...styles.statValue, color: "#10b981" }}>
                  {communityApproved.length}
                </strong>
              </div>
            </div>

            <div style={styles.orderList}>
              {communityPending.length === 0 ? (
                <div style={styles.emptyState}>
                  <FileText size={48} style={{ opacity: 0.3, marginBottom: 15 }} />
                  <p>Hiện chưa có bài cộng đồng nào chờ duyệt.</p>
                </div>
              ) : (
                communityPending.map((post) => (
                  <div key={post.firebaseKey} style={styles.orderCard}>
                    <div style={styles.orderInfo}>
                      <div style={styles.orderMainHeader}>
                        <span style={styles.orderId}>#{post.id}</span>
                        <span style={styles.orderTime}>
                          {post.createdAt
                            ? new Date(post.createdAt).toLocaleString("vi-VN")
                            : "Vừa xong"}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontWeight: "600",
                            background: "#fef3c7",
                            color: "#b45309",
                          }}
                        >
                          Chờ duyệt cộng đồng
                        </span>
                      </div>
                      <h4 style={styles.customerName}>{post.title}</h4>
                      <div style={styles.customerContact}>
                        <span>👤 {post.author}</span>
                        <span style={{ marginLeft: 15 }}>🏷 #{post.tag}</span>
                      </div>
                      <p style={styles.postContent}>{post.content}</p>
                    </div>
                    <div style={styles.orderPriceWrap}>
                      <div style={styles.actionGroup}>
                        <button
                          style={styles.btnApprove}
                          onClick={() => approveCommunityPost(post)}
                        >
                          <CheckCircle size={16} /> Duyệt bài
                        </button>
                        <button
                          style={styles.btnDelete}
                          onClick={() => rejectCommunityPost(post)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: "500",
  color: "#8a8a93",
  marginBottom: "8px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  fontFamily: '"Montserrat", sans-serif',
};

const styles = {
  toastContainer: {
    position: "absolute",
    zIndex: "9999",
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    willChange: "backdrop-filter, transform",
    transformStyle: "preserve-3d",
    backfaceVisibility: "hidden",
    backgroundColor: "rgba(255, 255, 255, 0.38)",
    backdropFilter: "blur(12px) saturate(180%)",
    WebkitBackdropFilter: "blur(12px) saturate(180%)",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    borderRadius: "50px",
    padding: "14px 28px",
    color: "#000000",
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "14px",
    fontWeight: "500",
    letterSpacing: "0.5px",
    textAlign: "center",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
    width: "max-content",
    maxWidth: "85vw",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.4",
  },
  toastContainerSuccess: {
    backgroundColor: "rgba(255, 255, 255, 0.25)",
    border: "1px solid rgba(0, 0, 0, 0.5)",
    color: "#000000",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
  },
  toastContainerWarning: {
    backgroundColor: "rgba(212, 175, 55, 0.25)",
    border: "1px solid rgba(212, 175, 55, 0.5)",
    color: "#000000",
    boxShadow: "0 8px 32px rgba(212, 175, 55, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
  },
  toastContainerError: {
    backgroundColor: "rgba(239, 68, 68, 0.25)",
    border: "1px solid rgba(239, 68, 68, 0.5)",
    color: "#000000",
    boxShadow: "0 8px 32px rgba(239, 68, 68, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
  },
  adminContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#f3f4f6",
  },
  loginCard: {
    background: "#fff",
    padding: "40px",
    borderRadius: "16px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
    width: "100%",
    maxWidth: "400px",
    textAlign: "center",
  },
  iconCircle: {
    width: "50px",
    height: "50px",
    background: "#1e1e24",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 auto 20px",
  },
  loginTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  loginSubtitle: {
    fontSize: "14px",
    color: "#6b7280",
    marginBottom: "24px",
  },
  adminInput: {
    width: "100%",
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    marginBottom: "16px",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  adminInputError: {
    borderColor: "#ef4444",
  },
  inlineErrorBox: {
    display: "flex",
    alignItems: "center",
    color: "#ef4444",
    fontSize: "12px",
    marginBottom: "16px",
    justifyContent: "center",
  },
  btnAction: {
    width: "100%",
    padding: "12px",
    background: "#1e1e24",
    color: "#fff",
    borderRadius: "8px",
    fontWeight: "bold",
    cursor: "pointer",
    border: "none",
  },
  backText: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#6b7280",
    marginTop: "20px",
    cursor: "pointer",
  },
  dashboardContainer: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    padding: "24px",
    fontFamily: '"Montserrat", sans-serif',
  },
  adminHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#fff",
    padding: "16px 24px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
    marginBottom: "24px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  logoBadge: {
    background: "#1e1e24",
    padding: "10px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    margin: 0,
  },
  headerSubtitle: {
    fontSize: "13px",
    color: "#6b7280",
  },
  tabGroup: {
    display: "flex",
    gap: "8px",
  },
  btnTab: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 16px",
    border: "none",
    background: "transparent",
    color: "#6b7280",
    cursor: "pointer",
    borderRadius: "8px",
    fontWeight: "500",
    transition: "all 0.2s",
  },
  btnTabActive: {
    background: "#f3f4f6",
    color: "#1e1e24",
    fontWeight: "600",
  },
  btnLogout: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 16px",
    background: "#fee2e2",
    color: "#ef4444",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "500",
  },
  contentWrap: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  statsRow: {
    display: "flex",
    marginBottom: "24px",
  },
  statBox: {
    flex: 1,
    background: "#fff",
    padding: "20px",
    borderRadius: "12px",
    borderLeft: "4px solid #f59e0b",
    boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
  },
  statLabel: {
    display: "block",
    fontSize: "14px",
    color: "#6b7280",
    marginBottom: "8px",
  },
  statValue: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#f59e0b",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px",
    background: "#fff",
    borderRadius: "12px",
    color: "#9ca3af",
    border: "1px dashed #d1d5db",
  },
  orderList: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  orderCard: {
    background: "#fff",
    padding: "20px",
    borderRadius: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
  },
  orderInfo: {
    flex: 1,
  },
  orderMainHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  orderId: {
    fontWeight: "bold",
    fontSize: "16px",
  },
  orderTime: {
    fontSize: "13px",
    color: "#6b7280",
  },
  customerName: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "4px",
    margin: 0,
  },
  customerContact: {
    fontSize: "14px",
    color: "#4b5563",
    marginBottom: "12px",
  },
  itemList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  itemTag: {
    fontSize: "13px",
    background: "#f3f4f6",
    padding: "4px 8px",
    borderRadius: "6px",
  },
  orderPriceWrap: {
    textAlign: "right",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "12px",
  },
  priceVal: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#1e1e24",
  },
  actionGroup: {
    display: "flex",
    gap: "8px",
  },
  btnApprove: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "#10b981",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
  },
  btnDelete: {
    background: "#fee2e2",
    color: "#ef4444",
    border: "none",
    padding: "8px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  completedToggle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "16px",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "600",
  },
  completedCard: {
    background: "#f9fafb",
    padding: "20px",
    borderRadius: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "1px solid #e5e7eb",
    marginBottom: "12px",
  },
  successBadge: {
    fontSize: "11px",
    color: "#10b981",
    background: "#d1fae5",
    padding: "2px 8px",
    borderRadius: "12px",
    fontWeight: "600",
  },
  gridTwoColumns: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
  },
  stickyColumn: {
    position: "sticky",
    top: "24px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "16px",
  },
  formContainer: {
    background: "#fff",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
  },
  uploadSection: {
    marginBottom: "24px",
  },
  uploadLabel: {
    display: "block",
    cursor: "pointer",
  },
  uploadPlaceholder: {
    border: "2px dashed #d1d5db",
    borderRadius: "12px",
    padding: "30px",
    textAlign: "center",
    background: "#f9fafb",
  },
  uploadIconCircle: {
    width: "40px",
    height: "40px",
    background: "#fff",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 auto 12px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
  },
  previewBox: {
    marginTop: "16px",
    position: "relative",
  },
  previewImg: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
    borderRadius: "12px",
  },
  previewTag: {
    position: "absolute",
    top: "10px",
    left: "10px",
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    fontSize: "11px",
    padding: "4px 8px",
    borderRadius: "4px",
  },
  subFormTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: "16px",
    color: "#4b5563",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  inputStyle: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  selectStyle: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box",
    background: "#fff",
  },
  textareaStyle: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box",
    minHeight: "80px",
    resize: "vertical",
  },
  btnSubmitForm: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: "100%",
    padding: "12px",
    background: "#1e1e24",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: "bold",
    cursor: "pointer",
    marginTop: "24px",
  },
  flowerItemGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  flowerMiniCard: {
    background: "#fff",
    padding: "16px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
    border: "1px solid #e5e7eb",
  },
  flowerCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "12px",
  },
  flowerCardTitle: {
    fontSize: "15px",
    fontWeight: "bold",
    margin: 0,
  },
  flowerCardSub: {
    fontSize: "12px",
    color: "#6b7280",
  },
  btnDeleteMini: {
    background: "#fee2e2",
    color: "#ef4444",
    border: "none",
    padding: "6px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  flowerMetaDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "12px",
  },
  metaLine: {
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    color: "#4b5563",
  },
  flowerIdBadge: {
    fontSize: "11px",
    background: "#f3f4f6",
    padding: "4px 8px",
    borderRadius: "4px",
    color: "#6b7280",
    display: "inline-block",
  },
  postCard: {
    background: "#fff",
    padding: "20px",
    borderRadius: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
    border: "1px solid #e5e7eb",
  },
  postMainInfo: {
    flex: 1,
  },
  postCategoryBadge: {
    fontSize: "11px",
    background: "#e0e7ff",
    color: "#4338ca",
    padding: "4px 8px",
    borderRadius: "4px",
    fontWeight: "600",
    display: "inline-block",
    marginBottom: "8px",
  },
  postTitleText: {
    fontSize: "16px",
    fontWeight: "bold",
    margin: "0 0 8px 0",
  },
  postMetaBottom: {
    fontSize: "13px",
    color: "#6b7280",
  },
  postContent: {
    fontSize: "14px",
    color: "#4b5563",
    marginTop: "12px",
    background: "#f9fafb",
    padding: "12px",
    borderRadius: "8px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000,
  },
  modalBox: {
    background: "#fff",
    padding: "30px",
    borderRadius: "16px",
    width: "90%",
    maxWidth: "400px",
    textAlign: "center",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
  },
  modalHeaderIcon: {
    background: "#fee2e2",
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 auto 16px",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "12px",
    margin: 0,
  },
  modalMessage: {
    fontSize: "14px",
    color: "#4b5563",
    marginBottom: "24px",
  },
  modalButtonGroup: {
    display: "flex",
    gap: "12px",
  },
  btnModalCancel: {
    flex: 1,
    padding: "12px",
    background: "#f3f4f6",
    color: "#4b5563",
    border: "none",
    borderRadius: "8px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnModalConfirm: {
    flex: 1,
    padding: "12px",
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: "600",
    cursor: "pointer",
  },
  toastNotification: {
    position: "fixed",
    top: "24px",
    right: "24px",
    padding: "16px 24px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    color: "#fff",
    fontWeight: "500",
    zIndex: 10000,
    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
  },
  toastSuccess: {
    background: "#10b981",
  },
  toastError: {
    background: "#ef4444",
  },
};